/**
 * Keep a pop-out's page from moving the main window.
 *
 * The app's current page lives in localStorage.activeTab, and the main window
 * follows `storage` events on that key. About a dozen components navigate by
 * writing the key directly (then dispatching a synthetic `storage` event or a
 * `changeTab` event). In a pop-out that write would reach every other window —
 * the main window would jump to whatever page a tab inside a pop-out opened.
 *
 * So in a pop-out (the tab host, or a page inside it) the `activeTab` key is
 * virtualised: reads and writes hit a per-window value, never localStorage.
 * Everything else in localStorage — the auth token, the active org — stays
 * shared, which is what lets a pop-out be signed in as the same user.
 */
import {
  isAnyPopoutLocation,
  migrateLegacyMessagingLocation,
  parsePagePopoutLocation,
  isAppPopoutLocation,
  type AppTabSpec,
} from './popout';

export const SHIELDED_KEY = 'activeTab';

let installed = false;

export function installActiveTabShield(seed: string | null, storage: Storage = window.localStorage): void {
  if (installed) return;
  installed = true;
  let value: string | null = seed;
  const proto = Object.getPrototypeOf(storage) as Storage;
  const origGet = proto.getItem;
  const origSet = proto.setItem;
  const origRemove = proto.removeItem;
  proto.getItem = function getItem(this: Storage, key: string) {
    if (this === storage && key === SHIELDED_KEY) return value;
    return origGet.call(this, key);
  };
  proto.setItem = function setItem(this: Storage, key: string, v: string) {
    if (this === storage && key === SHIELDED_KEY) {
      value = String(v);
      return;
    }
    origSet.call(this, key, v);
  };
  proto.removeItem = function removeItem(this: Storage, key: string) {
    if (this === storage && key === SHIELDED_KEY) {
      value = null;
      return;
    }
    origRemove.call(this, key);
  };
}

export type PopoutBoot =
  | { kind: null }
  | { kind: 'app' }
  | { kind: 'page'; page: AppTabSpec | null };

/**
 * Run once, at module load, before App reads the location: rewrite a
 * first-release messaging URL, decide which pop-out (if any) this window is,
 * and shield activeTab in pop-outs. A page pop-out starts on its own page.
 */
export function bootPopoutLocation(win: Window = window): PopoutBoot {
  if (typeof win === 'undefined' || !win.location) return { kind: null };
  migrateLegacyMessagingLocation(win);
  if (!isAnyPopoutLocation(win.location)) return { kind: null };
  if (isAppPopoutLocation(win.location)) {
    installActiveTabShield(null, win.localStorage);
    return { kind: 'app' };
  }
  const page = parsePagePopoutLocation(win.location);
  installActiveTabShield(page?.tab ?? null, win.localStorage);
  return { kind: 'page', page };
}
